import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Flower2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const TYPES = [
  { v: "Life Member", d: "Lifetime association with the trust and all its activities." },
  { v: "Patron", d: "Support our seva with a generous annual contribution." },
  { v: "Volunteer", d: "Offer your time and skills to our programs." },
  { v: "Donor", d: "Contribute towards welfare and ashram upkeep." },
];
const empty = { full_name: "", mobile: "", email: "", membership_type: "Life Member" };

export default function MembershipPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const { t } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/members", { ...form, status: "Pending" });
      setDone(data);
      toast.success(t("Application submitted!"));
      setForm(empty);
    } catch (err) {
      toast.error(err.response ? (formatApiError(err.response.data?.detail) || t("Submission failed")) : t("Could not send. Please check your connection and try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="public-membership-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("Be a part of us")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("Membership")}</h1>
          <p className="text-white/70 mt-3 max-w-xl">{t("Join the Hatha Vidya family and support yoga, spirituality and community welfare in Kutch.")}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl font-semibold text-[#194324]">{t("Choose your path of seva")}</h2>
          <div className="mt-6 space-y-4">
            {TYPES.map((ty) => (
              <button key={ty.v} type="button" onClick={() => setForm({ ...form, membership_type: ty.v })} className={`w-full text-left rounded-xl border p-5 transition-all ${form.membership_type === ty.v ? "border-[#C59B27] bg-[#F9F3E3] shadow-sm" : "border-[#E2DAD0] bg-white hover:border-[#C59B27]"}`} data-testid={`membership-type-${ty.v}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#194324]">{t(ty.v)}</span>
                  {form.membership_type === ty.v && <CheckCircle2 className="h-5 w-5 text-[#C59B27]" />}
                </div>
                <p className="text-sm text-[#5C7264] mt-1">{t(ty.d)}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          {done ? (
            <div className="bg-white rounded-2xl border border-[#E2DAD0] p-8 text-center" data-testid="membership-success">
              <div className="inline-flex h-16 w-16 rounded-full bg-[#E8F0EA] items-center justify-center mb-4"><CheckCircle2 className="h-8 w-8 text-[#194324]" /></div>
              <h3 className="font-display text-2xl font-semibold text-[#194324]">{t("Application Received!")}</h3>
              <p className="text-[#5C7264] mt-2">{t("Thank you")}, {done.full_name}. {t("Your membership application is pending review by the trust.")}</p>
              <div className="mt-5 bg-[#F9F3E3] rounded-xl p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-[#B38728]">{t("Your Member ID")}</div>
                <div className="text-2xl font-bold text-[#194324] font-mono mt-1">{done.member_id}</div>
              </div>
              <Button onClick={() => setDone(null)} variant="outline" className="mt-6 rounded-full">{t("Submit another")}</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E2DAD0] p-8 space-y-5">
              <div className="flex items-center gap-2 text-[#194324]"><Flower2 className="h-5 w-5 text-[#C59B27]" /><h3 className="font-display text-2xl font-semibold">{t("Membership Form")}</h3></div>
              <div className="space-y-2"><Label>{t("Full Name")}</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.targety.value })} required data-testid="membership-form-name" /></div>
              <div className="space-y-2"><Label>{t("Mobile Number")}</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.targety.value })} required data-testid="membership-form-mobile" /></div>
              <div className="space-y-2"><Label>{t("Email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.targety.value })} required data-testid="membership-form-email" /></div>
              <div className="space-y-2"><Label>{t("Selected Membership")}</Label><Input value={t(form.membership_type)} readOnly className="bg-[#F2EEE5] font-medium text-[#194324]" data-testid="membership-form-type" /></div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#194324] hover:bg-[#2D623A] text-white font-semibold" data-testid="membership-form-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Submit Application")}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
